import type { IAoeCart, IAoeCartItem } from '@/types/CartTypes'
import type { ICategoryItems } from '@/types/CategoryTypes'
import { GetCategoriesFromLocalStorageOrApi } from '@/services/CategoryService'
import { GetCachedProductDetails, GetCachedSkuDetail } from '@/services/SkuService'
import type { IMatomoItem } from '@/analytics/index'
import type { IOrderDetailGetItemSku } from '@/types/OrderTypes'
import type { IProductExWithSkuExsViewModel } from '@/types/SkuTypes'

function getCategoryTrail(
  categories: ICategoryItems[],
  categoryCode: string,
): string[] {
  for (const categoryItem of categories) {
    if (categoryItem.category.categoryCode === categoryCode) {
      return [categoryItem.category.categoryName]
    }

    const matchingChild = categoryItem.categoryChildren.find(
      child => child.categoryCode === categoryCode,
    )
    if (matchingChild) {
      return [categoryItem.category.categoryName, matchingChild.categoryName]
    }
  }

  const categoryMap = new Map(
    categories.flatMap(categoryItem => [
      [categoryItem.category.categoryCode, categoryItem.category],
      ...categoryItem.categoryChildren.map(child => [child.categoryCode, child] as const),
    ]),
  )

  const trail: string[] = []
  let currentCategory = categoryMap.get(categoryCode)

  while (currentCategory && trail.length < 3) {
    trail.unshift(currentCategory.categoryName)
    if (!currentCategory.categoryCodeParent) {
      break
    }
    currentCategory = categoryMap.get(currentCategory.categoryCodeParent)
  }

  return trail
}

async function buildMatomoItem(
  item: IAoeCartItem,
  categories: ICategoryItems[],
  options?: {
    coupon?: string
  },
): Promise<IMatomoItem> {
  const categoryTrail = getCategoryTrail(categories, item.categoryCode)
  const skuDetail = await GetCachedSkuDetail(item.skuCode)

  return {
    item_id: item.skuCode,
    item_name: item.skuName,
    affiliation: 'Pampered Chef EU',
    item_brand: 'Pampered Chef',
    item_category: categoryTrail[0] ?? item.categoryCode,
    item_category2: categoryTrail[1],
    item_category3: categoryTrail[2],
    item_variant: skuDetail?.friendlyName || skuDetail?.skuName || item.skuName,
    price: item.retailPrice,
    quantity: item.quantity,
    ...(options?.coupon ? { coupon: options.coupon } : {}),
    ...(item.totalDiscountAmount ? { discount: item.totalDiscountAmount } : {}),
  }
}

async function buildPurchaseMatomoItem(
  item: IOrderDetailGetItemSku,
  categories: ICategoryItems[],
  productDetailsByProductCode: Map<string, IProductExWithSkuExsViewModel>,
  options?: {
    coupon?: string
  },
): Promise<IMatomoItem> {
  const productDetail = productDetailsByProductCode.get(item.productCode)
  const categoryCode = productDetail?.categoryCode || item.productCode
  const categoryTrail = getCategoryTrail(categories, categoryCode)
  const skuDetail = await GetCachedSkuDetail(item.skuCode)

  return {
    item_id: item.skuCode,
    item_name: item.productName,
    affiliation: 'Pampered Chef EU',
    item_brand: 'Pampered Chef',
    item_category: categoryTrail[0] ?? '',
    item_category2: categoryTrail[1] ?? '',
    item_category3: categoryTrail[2] ?? '',
    item_variant: skuDetail?.friendlyName || skuDetail?.skuName || item.productName,
    price: item.price,
    quantity: item.quantity,
    ...(options?.coupon ? { coupon: options.coupon } : {}),
    ...(item.totalDiscountAmount ? { discount: item.totalDiscountAmount } : {}),
  }
}

export async function buildMatomoItemsFromCart(
  cart: IAoeCart,
  fallbackOrderTypeId: string = '',
): Promise<IMatomoItem[]> {
  const matomoOrderTypeId = cart.orderTypeId || fallbackOrderTypeId
  if (!matomoOrderTypeId) {
    return cart.items.map(item => ({
      item_id: item.skuCode,
      item_name: item.skuName,
      affiliation: 'Pampered Chef EU',
      item_brand: 'Pampered Chef',
      item_category: item.categoryCode,
      item_variant: item.skuName,
      price: item.retailPrice,
      quantity: item.quantity,
      ...(cart.couponCode ? { coupon: cart.couponCode } : {}),
      ...(item.totalDiscountAmount ? { discount: item.totalDiscountAmount } : {}),
    }))
  }

  const categories =
    (await GetCategoriesFromLocalStorageOrApi({ orderTypeId: matomoOrderTypeId })) ?? []

  return await Promise.all(
    cart.items.map(item => buildMatomoItem(item, categories, { coupon: cart.couponCode })),
  )
}

export async function buildMatomoItemsFromOrder(
  items: IOrderDetailGetItemSku[],
  orderTypeId: string,
  options?: {
    coupon?: string
  },
): Promise<IMatomoItem[]> {
  if (!orderTypeId) {
    return items.map(item => ({
      item_id: item.skuCode,
      item_name: item.productName,
      affiliation: 'Pampered Chef EU',
      item_brand: 'Pampered Chef',
      item_category: '',
      item_category2: '',
      item_category3: '',
      item_variant: item.productName,
      price: item.price,
      quantity: item.quantity,
      ...(options?.coupon ? { coupon: options.coupon } : {}),
      ...(item.totalDiscountAmount ? { discount: item.totalDiscountAmount } : {}),
    }))
  }

  const categories =
    (await GetCategoriesFromLocalStorageOrApi({ orderTypeId })) ?? []
  const productDetails = await GetCachedProductDetails(
    orderTypeId,
    Array.from(new Set(items.map(item => item.productCode))),
  )
  const productDetailsByProductCode = new Map(
    productDetails.map(productDetail => [productDetail.productCode, productDetail] as const),
  )

  return await Promise.all(
    items.map(item => buildPurchaseMatomoItem(item, categories, productDetailsByProductCode, options)),
  )
}
